import React, { useEffect, useMemo, useState } from 'react';
import { App as AntApp, Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { EditOutlined, HistoryOutlined, LockOutlined, PlusOutlined, ReloadOutlined, TeamOutlined, UserDeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import './UserManagementPanel.css';

const { Text } = Typography;

export default function UserManagementPanel({ onOpenAudit }) {
  const { user: currentUser } = useAuth();
  const { message } = AntApp.useApp();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [usersResult, rolesResult] = await Promise.all([
        userService.getAllUsers({ include_inactive: true, limit: 1000 }),
        userService.getRoles()
      ]);
      setUsers(usersResult.data || []);
      setRoles(rolesResult.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถโหลดรายชื่อผู้ใช้งานได้');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userModalOpen) return;
    if (editingUser) {
      form.setFieldsValue({
        name: editingUser.name || '',
        email: editingUser.email || '',
        phone: editingUser.phone || '',
        address: editingUser.address || '',
        role_id: Number(editingUser.role_id)
      });
    } else {
      form.resetFields();
    }
  }, [editingUser, form, userModalOpen]);

  const filteredUsers = useMemo(() => users.filter(user => {
    const matchesText = `${user.name} ${user.email} ${user.role_name}`.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = status === 'all' || (status === 'active' ? Boolean(user.is_active) : !Boolean(user.is_active));
    return matchesText && matchesStatus;
  }), [users, search, status]);
  const activeCount = users.filter(user => Boolean(user.is_active)).length;
  const isSuperAdmin = String(currentUser?.role || currentUser?.roleDetails?.code || '').toUpperCase() === 'SUPER_ADMIN';
  const isProtectedUser = user => !isSuperAdmin && String(user.role_code || '').toUpperCase() === 'SUPER_ADMIN';
  const canAssignRole = role => isSuperAdmin || !['SUPER_ADMIN', 'ADMIN'].includes(String(role.role_code || '').toUpperCase());

  const openAdd = () => {
    setEditingUser(null);
    setUserModalOpen(true);
  };
  const openEdit = user => {
    setEditingUser(user);
    setUserModalOpen(true);
  };
  const saveUser = async values => {
    setSavingUser(true);
    try {
      const payload = {
        ...values,
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone?.trim() || '',
        address: values.address?.trim() || '',
        role_id: Number(values.role_id)
      };
      if (editingUser) await userService.updateUser(editingUser.id, payload);
      else await userService.createUser(payload);
      message.success(editingUser ? 'บันทึกข้อมูลผู้ใช้แล้ว' : 'เพิ่มผู้ใช้งานแล้ว');
      setUserModalOpen(false);
      setEditingUser(null);
      await load();
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      message.error(validationMessage || error.response?.data?.message || 'บันทึกข้อมูลผู้ใช้ไม่สำเร็จ');
    } finally {
      setSavingUser(false);
    }
  };
  const changeStatus = async (user, is_active) => {
    try { await userService.setUserStatus(user.id, is_active); message.success(is_active ? 'เปิดใช้งานบัญชีแล้ว' : 'ปิดใช้งานบัญชีแล้ว'); await load(); }
    catch (error) { message.error(error.response?.data?.message || 'เปลี่ยนสถานะบัญชีไม่สำเร็จ'); }
  };
  const resetPassword = async values => {
    setSavingPassword(true);
    try { await userService.changePassword(passwordUser.id, { newPassword: values.newPassword }); message.success('เปลี่ยนรหัสผ่านแล้ว'); setPasswordModalOpen(false); }
    catch (error) { message.error(error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ'); }
    finally { setSavingPassword(false); }
  };

  const columns = [
    { title: 'ผู้ใช้งาน', key: 'user', render: (_, user) => <Space direction="vertical" size={0}><Text strong>{user.name}</Text><Text type="secondary">{user.email}</Text></Space> },
    { title: 'บทบาท', dataIndex: 'role_name', render: (value, user) => <Tag color={user.role_code === 'SUPER_ADMIN' ? 'red' : 'blue'}>{value}</Tag> },
    { title: 'ติดต่อ', dataIndex: 'phone', render: value => value || '-' },
    { title: 'เข้าสู่ระบบล่าสุด', dataIndex: 'last_login', render: value => value ? dayjs(value).format('DD/MM/YYYY HH:mm') : 'ยังไม่เคยเข้าสู่ระบบ' },
    { title: 'สถานะ', dataIndex: 'is_active', render: value => <Tag color={value ? 'success' : 'default'}>{value ? 'ใช้งาน' : 'ปิดใช้งาน'}</Tag> },
    { title: 'จัดการ', key: 'actions', render: (_, user) => { const locked = isProtectedUser(user); const isSelf = Number(currentUser?.id) === Number(user.id); return <Space wrap>{onOpenAudit && <Button size="small" icon={<HistoryOutlined />} onClick={() => onOpenAudit(user.id)}>ประวัติ</Button>}<Button size="small" disabled={locked} icon={<EditOutlined />} onClick={() => openEdit(user)}>แก้ไข</Button><Button size="small" disabled={locked} icon={<LockOutlined />} onClick={() => { setPasswordUser(user); passwordForm.resetFields(); setPasswordModalOpen(true); }}>รหัสผ่าน</Button><Popconfirm disabled={locked || isSelf} title={`${user.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}บัญชี ${user.name}?`} onConfirm={() => changeStatus(user, !user.is_active)} okText="ยืนยัน" cancelText="ยกเลิก"><Button size="small" disabled={locked || isSelf} danger={Boolean(user.is_active)} icon={<UserDeleteOutlined />}>{user.is_active ? 'ปิดบัญชี' : 'เปิดบัญชี'}</Button></Popconfirm></Space> } },
  ];

  return <div className="user-management-panel">
    <Card className="user-management-hero" bordered={false}>
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col><Space align="start"><TeamOutlined className="user-management-hero__icon" /><div><Typography.Title level={3}>จัดการผู้ใช้งาน</Typography.Title><Text type="secondary">สร้าง แก้ไข เปลี่ยนรหัสผ่าน และเปิด/ปิดบัญชีผู้ใช้งาน</Text></div></Space></Col>
        <Col><Space wrap><Button icon={<ReloadOutlined />} onClick={load}>รีเฟรช</Button><Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>เพิ่มผู้ใช้</Button></Space></Col>
      </Row>
      <Row gutter={[12, 12]} className="user-management-stats"><Col xs={12} md={6}><Statistic title="ผู้ใช้ทั้งหมด" value={users.length} suffix="คน" /></Col><Col xs={12} md={6}><Statistic title="กำลังใช้งาน" value={activeCount} suffix="คน" valueStyle={{ color: '#389e0d' }} /></Col><Col xs={12} md={6}><Statistic title="ปิดใช้งาน" value={users.length - activeCount} suffix="คน" valueStyle={{ color: '#8c8c8c' }} /></Col><Col xs={12} md={6}><Statistic title="บทบาทที่ใช้" value={new Set(users.map(user => user.role_id)).size} suffix="บทบาท" /></Col></Row>
    </Card>
    <Card bordered={false} className="user-management-table"><Row gutter={[12, 12]}><Col xs={24} md={14}><Input.Search placeholder="ค้นหาชื่อ อีเมล หรือบทบาท" allowClear onChange={event => setSearch(event.target.value)} /></Col><Col xs={24} md={6}><Select value={status} onChange={setStatus} style={{ width: '100%' }} options={[{ value: 'all', label: 'ทุกสถานะ' }, { value: 'active', label: 'กำลังใช้งาน' }, { value: 'inactive', label: 'ปิดใช้งาน' }]} /></Col></Row><Table className="user-management-table__grid" loading={loading} rowKey="id" columns={columns} dataSource={filteredUsers} scroll={{ x: 900 }} pagination={{ pageSize: 10, showSizeChanger: true, showTotal: total => `ทั้งหมด ${total} คน` }} /></Card>
    <Modal title={editingUser ? `แก้ไขผู้ใช้: ${editingUser.name}` : 'เพิ่มผู้ใช้งานใหม่'} open={userModalOpen} onCancel={() => { setUserModalOpen(false); setEditingUser(null); }} onOk={() => form.submit()} confirmLoading={savingUser} okText={editingUser ? 'บันทึก' : 'เพิ่มผู้ใช้'} cancelText="ยกเลิก" destroyOnClose>
      <Form form={form} layout="vertical" onFinish={saveUser} preserve={false}>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item name="name" label="ชื่อ-นามสกุล" rules={[{ required: true, message: 'กรุณาระบุชื่อผู้ใช้งาน' }]}><Input /></Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label="อีเมล" rules={[{ required: true, type: 'email', message: 'ระบุอีเมลให้ถูกต้อง' }]}><Input /></Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="phone" label={editingUser ? 'เบอร์โทร (ไม่บังคับ)' : 'เบอร์โทร'} rules={editingUser ? [] : [{ required: true, message: 'กรุณาระบุเบอร์โทร' }]}><Input /></Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="role_id" label="บทบาท" rules={[{ required: true, message: 'กรุณาเลือกบทบาท' }]}>
              <Select
                disabled={Number(currentUser?.id) === Number(editingUser?.id)}
                options={roles
                  .filter(role => (role.is_active || Number(role.id) === Number(editingUser?.role_id)) && canAssignRole(role))
                  .map(role => ({ value: Number(role.id), label: `${role.role_name} (${role.role_code})`, disabled: !role.is_active }))}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="address" label={editingUser ? 'ที่อยู่ (ไม่บังคับ)' : 'ที่อยู่'} rules={editingUser ? [] : [{ required: true, message: 'กรุณาระบุที่อยู่' }]}><Input.TextArea rows={2} /></Form.Item>
          </Col>
          {!editingUser && <Col span={24}><Form.Item name="password" label="รหัสผ่านเริ่มต้น" rules={[{ required: true, min: 6, message: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' }]}><Input.Password /></Form.Item></Col>}
        </Row>
      </Form>
    </Modal>
    <Modal title={`เปลี่ยนรหัสผ่าน: ${passwordUser?.name || ''}`} open={passwordModalOpen} onCancel={() => setPasswordModalOpen(false)} onOk={() => passwordForm.submit()} confirmLoading={savingPassword} okText="บันทึกรหัสผ่าน" cancelText="ยกเลิก" destroyOnClose><Form form={passwordForm} layout="vertical" onFinish={resetPassword} preserve={false}><Form.Item name="newPassword" label="รหัสผ่านใหม่" rules={[{ required: true, min: 6, message: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' }]}><Input.Password autoComplete="new-password" /></Form.Item><Form.Item name="confirmPassword" label="ยืนยันรหัสผ่าน" dependencies={['newPassword']} rules={[{ required: true, message: 'กรุณายืนยันรหัสผ่าน' }, ({ getFieldValue }) => ({ validator: (_, value) => !value || getFieldValue('newPassword') === value ? Promise.resolve() : Promise.reject(new Error('รหัสผ่านไม่ตรงกัน')) })]}><Input.Password autoComplete="new-password" /></Form.Item></Form></Modal>
  </div>;
}
